import { PDFDocument } from 'pdf-lib';
import { DEFAULT_TOOL_SETTINGS, OBJ } from '../constants';
import { makeImage, makeShape, makeText } from './objects';
import { buildTranscriptTable, parseHtmlTable, parsePipeTable } from './transcriptTables';
import { mathToText } from './texToText';

/**
 * Turns an AI page layout (items on a 0–1000 grid, see the backend
 * `planLayoutFromImage`) into what the PDF editor opens: a blank one-page PDF
 * plus the editable objects drawn on it. Nothing is uploaded and no second
 * PDF is built — the objects are the document.
 */

const PAGE_W = 595.28; // A4 width in points

const loadImage = (src) => new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = src;
});

/** Copy a region (0–1000 grid) of the sample photo into its own PNG data URL. */
function cropRegion(img, it) {
    const sx = (it.x / 1000) * img.naturalWidth;
    const sy = (it.y / 1000) * img.naturalHeight;
    const sw = Math.max(1, (it.w / 1000) * img.naturalWidth);
    const sh = Math.max(1, (it.h / 1000) * img.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw);
    canvas.height = Math.round(sh);
    canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    return { src: canvas.toDataURL('image/png'), w: canvas.width, h: canvas.height };
}

/**
 * @param {{ aspect:number, items:object[] }} layout
 * @param {{ sampleImage?: string, name?: string }} opts `sampleImage` = data URL of the
 *   original photo, used to copy logos / barcodes into the page.
 * @returns {Promise<{ file: File, objects: object[] }>}
 */
export async function layoutToPdf(layout, { sampleImage = null, name = 'document' } = {}) {
    if (layout?.kind === 'text') return textToPdf(layout.text, name);
    const aspect = Number(layout?.aspect) > 0 ? Number(layout.aspect) : 1.414;
    const pageH = PAGE_W * aspect;

    const doc = await PDFDocument.create();
    doc.addPage([PAGE_W, pageH]);
    const bytes = await doc.save();
    const safe = String(name || 'document').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'document';
    const file = new File([bytes], `${safe}.pdf`, { type: 'application/pdf' });

    let img = null;
    if (sampleImage && (layout?.items || []).some((i) => i.t === 'image')) {
        try { img = await loadImage(sampleImage); } catch { img = null; }
    }

    const fx = (v) => (v / 1000) * PAGE_W;
    const fy = (v) => (v / 1000) * pageH;
    const objects = [];

    for (const it of layout?.items || []) {
        if (it.t === 'box') {
            objects.push(makeShape(OBJ.RECT, { x: fx(it.x), y: fy(it.y), w: fx(it.w), h: fy(it.h) }, {
                stroke: it.stroke || 'transparent',
                strokeWidth: Math.max(0.5, fx(it.sw || 1)),
                fill: it.fill || 'transparent',
            }));
        } else if (it.t === 'line') {
            const x1 = fx(it.x1); const x2 = fx(it.x2);
            const y1 = fy(it.y1); const y2 = fy(it.y2);
            objects.push(makeShape(OBJ.LINE, {
                x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1),
            }, {
                stroke: it.color, strokeWidth: Math.max(0.5, fx(it.w || 1)), fill: 'transparent',
                flipX: x2 < x1, flipY: y2 < y1,
            }));
        } else if (it.t === 'image' && img) {
            const crop = cropRegion(img, it);
            objects.push(makeImage({ x: fx(it.x), y: fy(it.y), w: fx(it.w), h: fy(it.h) }, crop.src, crop.w, crop.h));
        } else if (it.t === 'text') {
            const obj = makeText({ x: fx(it.x), y: fy(it.y), w: fx(it.w), h: fy(it.h) }, {
                ...DEFAULT_TOOL_SETTINGS.text,
                fontSize: Math.max(5, fy(it.size)),
                color: it.color || '#111111',
                bold: !!it.bold,
                italic: !!it.italic,
                align: it.align || 'left',
                lineHeight: 1.2,
            }, it.text);
            objects.push(obj);
        }
    }

    return { file, objects };
}

/* ------------------------------------------------------------------ text */

const A4_H = 841.89;
const MARGIN = 56;
const FONT_SIZE = 13;
const LINE_H = 1.5;

/** Width of a string in points at FONT_SIZE, for estimating how many lines it wraps to. */
function makeMeasurer(size, bold) {
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.font = `${bold ? '700 ' : ''}${size}px "Hind Siliguri", "Noto Sans Bengali", sans-serif`;
    return (str) => ctx.measureText(str).width;
}

/** How many visual lines `str` takes inside `width` points (greedy word wrap). */
function wrapCount(str, width, measure) {
    let lines = 1;
    let cur = 0;
    const space = measure(' ');
    for (const word of String(str).split(/\s+/)) {
        const w = measure(word);
        if (cur && cur + space + w > width) { lines += 1; cur = w; } else { cur += (cur ? space : 0) + w; }
    }
    return lines;
}

/**
 * A transcribed line with inline <b> / <i> / <u> tags → its plain text plus non-overlapping style
 * marks (the editor's per-run styles). Any other tag is dropped, so markup never shows on the page.
 */
function parseInline(line) {
    const KEY = { b: 'bold', strong: 'bold', i: 'italic', em: 'italic', u: 'underline' };
    // Any tag that is not bold / italic / underline (<span>, <br>…) goes first, so the offsets below stay true.
    line = line.replace(/<\/?(?!(?:b|strong|i|em|u)\s*>)[a-z][^>]*>/gi, '');
    const re = /<(\/?)(b|strong|i|em|u)\s*>/gi;
    let text = '';
    let last = 0;
    const open = {};
    const spans = [];
    let m;
    while ((m = re.exec(line))) {
        text += line.slice(last, m.index);
        last = m.index + m[0].length;
        const key = KEY[m[2].toLowerCase()];
        if (m[1]) {
            if (open[key] != null && text.length > open[key]) spans.push({ key, start: open[key], end: text.length });
            delete open[key];
        } else if (open[key] == null) {
            open[key] = text.length;
        }
    }
    text += line.slice(last);
    for (const [key, start] of Object.entries(open)) if (text.length > start) spans.push({ key, start, end: text.length });
    if (!spans.length) return { text, marks: [] };
    // Fold the spans into runs of one combined style.
    const flags = Array.from({ length: text.length }, () => ({}));
    for (const sp of spans) for (let i = sp.start; i < sp.end; i += 1) flags[i][sp.key] = true;
    const marks = [];
    let from = 0;
    const same = (a, b) => !!a.bold === !!b.bold && !!a.italic === !!b.italic && !!a.underline === !!b.underline;
    for (let i = 1; i <= text.length; i += 1) {
        if (i === text.length || !same(flags[i], flags[from])) {
            const style = {};
            if (flags[from].bold) style.bold = true;
            if (flags[from].italic) style.italic = true;
            if (flags[from].underline) style.underline = true;
            if (Object.keys(style).length) marks.push({ start: from, end: i, style });
            from = i;
        }
    }
    return { text, marks };
}

const STYLE_TAGS = { bold: 'b', italic: 'i', underline: 'u' };

/**
 * The OCR's line tags where they are NOT at the start of the line — which the
 * model writes despite being told not to, and which used to reach the page as
 * literal "[underline]" / "[right]" text:
 * - `[underline]word[/underline]` (bold / italic too) → that span styled;
 * - an unclosed `[underline]word` mid-line → that one word styled (how the
 *   model marks an underlined word in a sentence, as on an exam's "(a) invent");
 * - `…text [right]1 × 5 = 5` → `trail`, a part set flush right on the same line
 *   (the marks column of a question paper).
 * Any other stray tag is dropped.
 * @returns {{ line: string, trail: string|null }} still carrying <b>/<i>/<u> for `parseInline`
 */
function inlineTags(line) {
    let out = line.replace(
        /\[(bold|italic|underline)\]([\s\S]*?)\[\/\1\]/gi,
        (_, tag, inner) => `<${STYLE_TAGS[tag.toLowerCase()]}>${inner}</${STYLE_TAGS[tag.toLowerCase()]}>`,
    );
    out = out.replace(/\s*\[\/(center|right|bold|italic|underline)\]/gi, '');
    let trail = null;
    const at = out.search(/\[right\]/i);
    if (at > 0) {
        trail = out.slice(at).replace(/\[right\]/gi, '').trim() || null;
        out = out.slice(0, at).replace(/\s+$/, '');
    }
    const words = (text) => text
        .replace(/\[(bold|italic|underline)\]\s*([^\s<]+)/gi, (_, tag, word) => `<${STYLE_TAGS[tag.toLowerCase()]}>${word}</${STYLE_TAGS[tag.toLowerCase()]}>`)
        .replace(/\[(center|right|bold|italic|underline)\]\s*/gi, '');
    return { line: words(out), trail: trail && words(trail) };
}

/**
 * The OCR transcription → editable text on A4 pages. Plain flow: one text box per line /
 * paragraph, stacked top-down, new pages when the sheet is full. Tags at the start of a
 * line ([center] [right] [bold]) set alignment / weight, and the ones mid-line are read by
 * `inlineTags` (styled words, a flush-right part); blank lines are paragraph gaps;
 * a pipe table row is written as its cells separated by wide gaps.
 */
async function textToPdf(text, name) {
    const contentW = PAGE_W - MARGIN * 2;
    const items = [];
    const lines = String(text || '').split('\n');
    for (let i = 0; i < lines.length; i += 1) {
        let line = lines[i].replace(/\s+$/, '');
        // An HTML table (one <tr> per line) — gathered up to its </table>.
        if (/^\s*<table\b/i.test(line)) {
            const block = [line];
            while (!/<\/table>/i.test(block[block.length - 1]) && i + 1 < lines.length) { i += 1; block.push(lines[i]); }
            const table = parseHtmlTable(block.join('\n'));
            if (table) { items.push({ table }); continue; }
            continue; // unreadable markup: drop it rather than print tags
        }
        // Consecutive Markdown pipe rows.
        if (/^\s*\|.*\|\s*$/.test(line)) {
            const block = [line];
            while (i + 1 < lines.length && /^\s*\|.*\|\s*$/.test(lines[i + 1])) { i += 1; block.push(lines[i]); }
            const table = parsePipeTable(block);
            if (table) items.push({ table });
            continue;
        }
        if (!line.trim()) { items.push({ gap: true }); continue; }
        // TeX maths ($\frac{1}{x^3}$) → readable text (1/x³): the canvas cannot set KaTeX.
        line = mathToText(line);
        let align = 'left';
        let bold = false;
        let italic = false;
        let underline = false;
        for (;;) {
            const m = line.match(/^\[(center|right|bold|italic|underline)\]\s*/i);
            if (!m) break;
            const tag = m[1].toLowerCase();
            if (tag === 'bold') bold = true;
            else if (tag === 'italic') italic = true;
            else if (tag === 'underline') underline = true;
            else align = tag;
            line = line.slice(m[0].length);
        }
        const tagged = inlineTags(line);
        const inline = parseInline(tagged.line);
        const trail = tagged.trail ? parseInline(tagged.trail) : null;
        if (!inline.text.trim() && !trail?.text.trim()) continue;
        items.push({ text: inline.text, marks: inline.marks, align, bold, italic, underline, trail });
    }

    // One sample image is one page, so the text should land on ONE page: lay it out at full size and,
    // if it spills over, again smaller (type, table rows, gaps and margins together) — to the LARGEST
    // size that still fits, so the page is used and the type stays as readable as it can.
    const layout = async (scale) => {
        const margin = MARGIN * Math.max(0.7, scale);
        const w = PAGE_W - margin * 2;
        const size = FONT_SIZE * scale;
        const lh = size * LINE_H;
        const bottom = A4_H - margin;
        const out = [[]];
        let y = margin;
        for (const it of items) {
            if (it.gap) { y += lh * 0.6; continue; }
            if (it.table) {
                // eslint-disable-next-line no-await-in-loop
                const t = await buildTranscriptTable(it.table, { x: margin, y: 0, w }, { scale });
                if (y + t.h > bottom && out[out.length - 1].length) { out.push([]); y = margin; }
                t.x = margin;
                t.y = y;
                t.page = out.length - 1;
                out[out.length - 1].push(t);
                y += t.h + lh * 0.6;
                continue;
            }
            // A right-hand part ("1 × 5 = 5") gets its own box flush right, level
            // with the text's LAST line, and the text keeps clear of it.
            const trailW = it.trail?.text.trim() ? makeMeasurer(size, false)(it.trail.text) + 12 : 0;
            const textW = trailW ? Math.max(w * 0.4, w - trailW - size) : w;
            const n = it.text.trim() ? wrapCount(it.text, textW * 0.97, makeMeasurer(size, it.bold)) : 1;
            const h = n * lh;
            if (y + h > bottom && out[out.length - 1].length) { out.push([]); y = margin; }
            if (trailW) {
                const tail = makeText({ x: margin + w - trailW, y: y + (n - 1) * lh, w: trailW, h: lh }, {
                    ...DEFAULT_TOOL_SETTINGS.text,
                    fontSize: size,
                    align: 'right',
                    lineHeight: LINE_H,
                }, it.trail.text);
                if (it.trail.marks?.length) tail.marks = it.trail.marks;
                tail.plainBrackets = true;
                tail.page = out.length - 1;
                out[out.length - 1].push(tail);
            }
            if (!it.text.trim()) { y += h; continue; }
            const obj = makeText({ x: margin, y, w: textW, h }, {
                ...DEFAULT_TOOL_SETTINGS.text,
                fontSize: size,
                bold: it.bold,
                italic: it.italic,
                underline: it.underline,
                align: it.align,
                lineHeight: LINE_H,
            }, it.text);
            if (it.marks?.length) obj.marks = it.marks;
            // Read off a page, so its brackets are its own text, not fields.
            obj.plainBrackets = true;
            obj.page = out.length - 1;
            out[out.length - 1].push(obj);
            y += h;
        }
        // Everything stacked end to end, in units of one page's usable height — the size it would need.
        const usable = bottom - margin;
        return { pages: out, need: (out.length - 1) + (y - margin) / usable };
    };
    let result = await layout(1);
    if (result.pages.length > 1) {
        // The height does not shrink in step with the size (shorter type wraps into fewer lines), so a guess from the
        // first pass overshoots and leaves a third of the page empty. Bisect the scale instead: every step is a real
        // layout, and the one kept is always a layout that fits.
        const FLOOR = 0.5;
        const floor = await layout(FLOOR);
        if (floor.pages.length > 1) {
            result = floor; // too much for one page even at the smallest readable size: let it run on
        } else {
            let lo = FLOOR;
            let hi = 1;
            result = floor;
            for (let step = 0; step < 8; step += 1) {
                const mid = (lo + hi) / 2;
                // eslint-disable-next-line no-await-in-loop
                const tried = await layout(mid);
                if (tried.pages.length === 1) { result = tried; lo = mid; } else { hi = mid; }
            }
        }
    }
    const pages = result.pages;

    const doc = await PDFDocument.create();
    pages.forEach(() => doc.addPage([PAGE_W, A4_H]));
    const bytes = await doc.save();
    const safe = String(name || 'document').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'document';
    return { file: new File([bytes], `${safe}.pdf`, { type: 'application/pdf' }), objects: pages.flat() };
}
